import { useSyncExternalStore } from 'react';
import axios from '@/config/axios';

const GHANA = 'Ghana';

// A tiny module-scope store rather than a fetch inside an effect. The country is external, mutable
// state that lives outside React: it is resolved once per tab, shared by every component that asks,
// and it cannot change mid-visit. Never written on the server — the request starts on the first
// browser subscription — so one visitor's country can never leak into another's render.
let country: string | null = null;
let inFlight = false;
const listeners = new Set<() => void>();

function emit() {
    for (const listener of listeners) {
        listener();
    }
}

async function resolveCountry(): Promise<void> {
    try {
        const { data } = await axios.get<{ country: string }>('/api/visitor-country', {
            skipAuth: true,
            disableToast: true,
        });
        country = data?.country ?? '';
    } catch {
        // Geo is cosmetic here: on any failure keep the default variant, which is exactly what the
        // prerendered HTML already shows.
        country = '';
    }

    emit();
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);

    if (country === null && !inFlight) {
        inFlight = true;
        void resolveCountry();
    }

    return () => {
        listeners.delete(listener);
    };
}

const getSnapshot = (): boolean => country === GHANA;

// The server and the hydrating client must agree, and the country is unknown at both points.
const getServerSnapshot = (): boolean => false;

/**
 * Whether the visitor is in Ghana, resolved after hydration.
 *
 * The pages that need this are statically prerendered, so the country cannot be known while
 * rendering them — reading it on the server would opt the whole route out of static rendering,
 * which is exactly what this replaced (W8M-8239).
 */
export function useIsGhana(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
